import { useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Camera, Loader2, Mail, Trash2 } from "lucide-react";

function resizeImage(file, size = 256) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        const min = Math.min(img.width, img.height);
        const sx = (img.width - min) / 2;
        const sy = (img.height - min) / 2;
        ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [title, setTitle] = useState(user?.title || "");
  const [avatar, setAvatar] = useState(user?.avatar || "");
  const [saving, setSaving] = useState(false);

  const displayImg = avatar || user?.picture || "";
  const initials = (firstName || user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    try {
      setAvatar(await resizeImage(file));
    } catch {
      toast.error("Could not read that image.");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", { first_name: firstName, last_name: lastName, phone, title, avatar });
      setUser((u) => ({ ...u, ...data }));
      toast.success("Profile saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 animate-fade-up" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tighter">My Profile</h1>
        <p className="mt-1 text-sm text-muted-foreground">Manage your photo and contact details</p>
      </div>

      <div className="rounded-lg border border-border bg-card p-6">
        <div className="flex flex-col items-center gap-4 sm:flex-row">
          <div className="relative">
            {displayImg ? (
              <img src={displayImg} alt="avatar" className="h-24 w-24 rounded-full object-cover ring-2 ring-border" data-testid="profile-avatar-img" />
            ) : (
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-secondary text-2xl font-black ring-2 ring-border">{initials}</div>
            )}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(var(--primary))] text-white shadow-md transition hover:opacity-90"
              data-testid="profile-avatar-upload"
              aria-label="Upload photo"
            >
              <Camera className="h-4 w-4" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="profile-avatar-input" />
          </div>
          <div className="text-center sm:text-left">
            <div className="font-heading text-lg font-bold">{[firstName, lastName].filter(Boolean).join(" ") || user?.name || "—"}</div>
            <div className="text-sm text-muted-foreground">{title || "Sales Representative"}</div>
            {avatar && (
              <Button variant="ghost" size="sm" className="mt-1 h-7 px-2 text-xs text-muted-foreground" onClick={() => setAvatar("")} data-testid="profile-avatar-remove">
                <Trash2 className="mr-1 h-3 w-3" />Remove photo
              </Button>
            )}
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="label-caps">First name</Label>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="mt-1 h-10" data-testid="profile-first-name" />
          </div>
          <div>
            <Label className="label-caps">Last name</Label>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} className="mt-1 h-10" data-testid="profile-last-name" />
          </div>
          <div>
            <Label className="label-caps">Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" className="mt-1 h-10" data-testid="profile-phone" />
          </div>
          <div>
            <Label className="label-caps">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Account Executive" className="mt-1 h-10" data-testid="profile-title" />
          </div>
          <div>
            <Label className="label-caps">Email</Label>
            <div className="mt-1 flex h-10 items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 text-sm text-muted-foreground" data-testid="profile-email">
              <Mail className="h-4 w-4" />{user?.email}
            </div>
          </div>
          <div>
            <Label className="label-caps">Role</Label>
            <div className="mt-1 flex h-10 items-center">
              <Badge variant="secondary" className="uppercase">{user?.role}</Badge>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="profile-save" className="min-w-32">
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
